import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import { set } from 'lodash';

import dashboardService from '../../../service/dashboard.service';
import { DashboardTypeList } from '../../../config/dashboard';
import { AutoCompleteSelectUI, FormLabelUI, SelectUI, DatePickerUI } from '../../Interface';
import BiReport from '../BiReport/BiReport';
import useUsers from '../../../context/UsersContext';
import useParam from '../../../context/ParamContext';
import { generateYears } from '../../../utils/dateHelper';
import { months } from '../../../config/months';
import { sxConfig } from './dashboard.style';

const weeks = Array.from({ length: 53 }, (_, index) => index + 1);

const Dashboard = () => {
  const { dashboardId } = useParams();
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();

  const [selectedDashboard, setSelectedDashboard] = useState({});
  const [dashboardType, setDashboardType] = useState({});
  const [yearVals, setYearVals] = useState([]);
  const [businessPartners, setBusinessPartners] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [autocompleteData, setAutocompleteData] = useState({
    businessPartners: [],
    responsibles: [],
  });

  const [filter, setFilter] = useState({});
  const [gridCount, setGridCount] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
  const [gridAvailableHeight, setGridAvailableHeight] = useState(360);
  const headerRef = useRef(null);
  const gridRef = useRef(null);

  const dashboardColumns = Number(dashboardType?.column) || 1;
  const reportCount = selectedDashboard?.dashboardReport?.length || 0;
  const responsiveColumnCap = viewportWidth >= 1600 ? 4 : viewportWidth >= 1200 ? 3 : viewportWidth >= 900 ? 2 : 1;
  const isDesktop = viewportWidth >= 1200;
  const gridColumns = Math.max(1, Math.min(dashboardColumns, responsiveColumnCap));
  const gridRows = Math.max(1, Math.ceil(Math.max(1, reportCount) / gridColumns));
  const shouldGridScroll = !isDesktop && gridColumns !== dashboardColumns;
  const gridGap = 8;
  const minCardHeight = shouldGridScroll ? 450 : 260;
  const availableGridHeight = Math.max(280, gridAvailableHeight);
  const cardHeight = Math.max(
    minCardHeight,
    Math.floor((availableGridHeight - (gridRows - 1) * gridGap) / gridRows) - 10
  );
  const reportHeight = Math.max(300, cardHeight - 22);

  const getDashboard = () => {
    dashboardService.getDashboard(dashboardId).then((data) => {
      setSelectedDashboard(data);
    });
  };

  const getBusinessPartners = () => {
    //removed slpcode filter 2024.10.22 CZI -> Folplast
    dashboardService.getBusinessPartners(/*user?.userSalesPerson?.slpCode*/).then((data) => {
      setBusinessPartners(data);
    });
  };

  const getResponsibles = () => {
    //@TODO responsible type if necessary
    dashboardService.getResponsibles('SALES').then((data) => {
      setResponsibles(data);
    });
  };

  const changeFilter = (key, value) => {
    setFilter((prev) => {
      const newFilter = { ...prev };
      set(newFilter, key, value);
      return newFilter;
    });
  };

  const defaultFilter = {
    businessPartner: null,
    responsible: null,
    year: '',
    quarter: '',
    week: '',
    month: '',
    interval: {
      from: null,
      to: null,
    },
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getParam();
    setFilter(defaultFilter);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const recalculateGridHeight = () => {
      if (!gridRef.current) return;

      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const gridTop = gridRef.current.getBoundingClientRect().top;
      const headerBottom = headerRef.current?.getBoundingClientRect().bottom ?? 0;
      const contentTop = Math.max(gridTop, headerBottom);
      const bottomSafeOffset = 0;
      const nextHeight = Math.floor(viewportHeight - contentTop - bottomSafeOffset);
      setGridAvailableHeight(Math.max(280, nextHeight));
    };

    recalculateGridHeight();
    window.addEventListener('resize', recalculateGridHeight);
    window.visualViewport?.addEventListener('resize', recalculateGridHeight);

    return () => {
      window.removeEventListener('resize', recalculateGridHeight);
      window.visualViewport?.removeEventListener('resize', recalculateGridHeight);
    };
  }, [gridColumns, gridRows, gridCount]);

  useEffect(() => {
    setYearVals(generateYears(params.DASHBOARFILTER_MIN_YEAR ?? 2021));
  }, [params]);

  useEffect(() => {
    if (Object.keys(user).length > 0) getBusinessPartners();
  }, [user]);

  useEffect(() => {
    if (businessPartners.length > 0) {
      const bpAutocompleteData = businessPartners.map((bp) => ({
        label: `${bp.companyId} - ${bp.companyName}`,
        value: bp.companyName,
      }));

      setAutocompleteData((prev) => {
        return { ...prev, businessPartners: bpAutocompleteData };
      });
    } else {
      setAutocompleteData((prev) => {
        return { ...prev, businessPartners: [] };
      });
    }
  }, [businessPartners]);

  useEffect(() => {
    if (responsibles.length > 0) {
      const responsiblesData = responsibles.map((responsible) => ({
        label: `${responsible.responsibleCode} - ${responsible.responsibleName}`,
        value: responsible.responsibleCode,
      }));

      setAutocompleteData((prev) => {
        return { ...prev, responsibles: responsiblesData };
      });
    } else {
      setAutocompleteData((prev) => {
        return { ...prev, responsibles: [] };
      });
    }
  }, [responsibles]);

  useEffect(() => {
    getDashboard();
    setFilter(defaultFilter);
  }, [dashboardId]);

  useEffect(() => {
    if (Object.keys(selectedDashboard).length > 0) {
      const findedDashboardType = DashboardTypeList.find((dt) => dt.value === selectedDashboard.type);
      setDashboardType(findedDashboardType);
      const { bpFilter, yearFilter, quarterFilter, weekFilter, monthFilter, intervalFilter, responsibleFilter } =
        selectedDashboard;

      let filterCount = [
        bpFilter,
        yearFilter,
        quarterFilter,
        weekFilter,
        monthFilter,
        intervalFilter,
        responsibleFilter,
      ].filter(Boolean).length;
      if (intervalFilter) filterCount++;
      setGridCount(filterCount);

      if (selectedDashboard.responsibleFilter) getResponsibles();
    }
  }, [selectedDashboard]);

  return (
    <>
      <div ref={headerRef} className="bg-gray-100 w-full px-3 md:fixed z-40">
        <div
          className={`absolute -top-14 right-[230px] grid gap-x-4 mx-4 mb-5 w-[1100px] justify-end`}
          style={{ gridTemplateColumns: `repeat(${gridCount}, minmax(0, 250px))` }}
        >
          {selectedDashboard?.bpFilter && (
            <div className="col-span-1 max-w-[300px]">
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Üp." textColor="text-white" />}
                variant="standard"
                onChange={(_e, newVal) => {
                  changeFilter('businessPartner', newVal);
                }}
                value={filter?.businessPartner ?? null}
                dataset={autocompleteData.businessPartners}
                autoFocus
                limitTags={1}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}

          {selectedDashboard?.responsibleFilter && (
            <div className="col-span-1">
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Felelős" textColor="text-white" />}
                variant="standard"
                onChange={(_e, newVal) => {
                  changeFilter('responsible', newVal ?? null);
                }}
                value={filter?.responsible}
                dataset={autocompleteData.responsibles}
                autoFocus
                limitTags={1}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}

          {selectedDashboard?.yearFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Év" textColor="text-white" />}
                option={yearVals}
                value={filter.year}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('year', value);
                }}
                onClear={() => {
                  changeFilter('year', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.quarterFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Negyedév" textColor="text-white" />}
                option={[1, 2, 3, 4]}
                value={filter.quarter}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('quarter', value);
                }}
                onClear={() => {
                  changeFilter('quarter', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.monthFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Hónap" textColor="text-white" />}
                option={months}
                value={filter.month}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('month', value);
                }}
                onClear={() => {
                  changeFilter('month', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.weekFilter && (
            <div className="col-span-1">
              <SelectUI
                label={<FormLabelUI text="Hét" textColor="text-white" />}
                option={weeks}
                value={filter.week}
                onChange={(e) => {
                  const value = e.target.value;
                  changeFilter('week', value);
                }}
                onClear={() => {
                  changeFilter('week', '');
                }}
                sx={sxConfig.fieldColor}
              />
            </div>
          )}
          {selectedDashboard?.intervalFilter && (
            <>
              <div className="col-span-1">
                <DatePickerUI
                  label={<FormLabelUI text="Kezdő dátum" textColor="text-white" />}
                  value={filter.interval.from}
                  onChange={(newDate) => {
                    changeFilter('interval.from', newDate ? newDate.startOf('day') : null);
                  }}
                  sx={sxConfig.fieldColor}
                />
              </div>
              <div className="col-span-1">
                <DatePickerUI
                  label={<FormLabelUI text="Vége dátum" textColor="text-white" />}
                  value={filter.interval.to}
                  onChange={(newDate) => {
                    changeFilter('interval.to', newDate ? newDate.startOf('day') : null);
                  }}
                  sx={sxConfig.fieldColor}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div
        ref={gridRef}
        className="grid overflow-x-hidden px-4 bg-gray-100 py-2"
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
          height: `${availableGridHeight}px`,
          gap: `${gridGap}px`,
          overflowY: shouldGridScroll ? 'auto' : 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {selectedDashboard?.dashboardReport?.map((report) => {
          return (
            <div
              className="col-span-1 overflow-hidden bg-white shadow sm:rounded-lg "
              style={{ height: `${cardHeight}px` }}
              key={uuidv4()}
            >
              <BiReport
                reportId={report.biReportId}
                height={reportHeight}
                filter={filter}
                dashboardData={selectedDashboard}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

export default Dashboard;
